import { useMemo, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { niceTicks, useWidth } from './UsageChart';
import { useConsoleI18n } from './bind';

// Statistics charts (SVG, tokens only, no chart library — same anatomy as pods/UsageChart): one y-axis, a legend for
// two or more series, direct labels at the line ends for up to four, crosshair tooltip, arrow keys move it; with
// `onSelect` a span can be dragged (pointer or touch) or marked (Shift + arrows, Enter) to zoom into it.
// Series colours follow the entity in a fixed order (`--nyk-series-1..6`), never the rank.

const H = 200;
const PAD = { top: 12, right: 12, bottom: 22, left: 60 };
const MAX_SERIES = 6;

export interface LinePoint {
  t: number;
  v: number | null;
}
export interface LineSeries {
  key: string;
  label: string;
  points: LinePoint[];
  /** Drawn dashed (e.g. the same metric in a comparison period); no direct label, not in the legend. */
  dashed?: boolean;
  /** Colour slot (0-based) instead of the series' position, so a dashed twin keeps its series' colour. */
  color?: number;
}
export interface Marker {
  t: number;
  label: string;
}

const timeLabel = (t: number, span: number) =>
  new Date(t).toLocaleString(
    undefined,
    span > 3 * 86_400_000
      ? { month: 'short', day: 'numeric' }
      : span > 86_400_000
        ? { month: 'short', day: 'numeric', hour: '2-digit' }
        : { hour: '2-digit', minute: '2-digit' }
  );

const tipTime = (t: number) =>
  new Date(t).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** A drag shorter than this (px) is a click, not a selection. */
const MIN_DRAG_PX = 6;

const seriesClass = (i: number) => `nyk-chart-series-${(i % MAX_SERIES) + 1}`;
const slot = (s: LineSeries, i: number) => s.color ?? i;

/** Labels at the line ends, dropped where they would overlap one already placed (the legend still names every line). */
function directLabels(
  series: LineSeries[],
  last: (pts: LinePoint[]) => LinePoint | null,
  x: (t: number) => number,
  y: (v: number) => number,
  right: number
) {
  const placed: { key: string; index: number; label: string; x: number; y: number }[] = [];
  series.forEach((s, index) => {
    const p = last(s.points);
    if (!p || p.v == null) return;
    const ly = y(p.v) - 6;
    if (placed.some((q) => Math.abs(q.y - ly) < 13)) return;
    placed.push({
      key: s.key,
      index,
      label: s.label.length > 24 ? `${s.label.slice(0, 23)}…` : s.label,
      x: Math.min(x(p.t), right) - 4,
      y: ly,
    });
  });
  return placed;
}

/** Legend chips (swatch + name), shown for two or more series so identity is never colour alone. */
export function Legend({
  items,
  dashedLabel,
}: {
  items: { key: string; label: string; color?: number }[];
  /** What the dashed lines are (one entry with a dashed swatch). */
  dashedLabel?: string;
}) {
  const { t } = useConsoleI18n();
  if (items.length < 2 && !dashedLabel) return null;
  return (
    <ul className="nyk-chart-legend" aria-label={t('chart.legend')}>
      {items.map((s, i) => (
        <li key={s.key}>
          <span className={`nyk-chart-swatch ${seriesClass(s.color ?? i)}`} aria-hidden="true" />
          {s.label}
        </li>
      ))}
      {dashedLabel && (
        <li>
          <span className="nyk-chart-swatch nyk-chart-swatch-dashed" aria-hidden="true" />
          {dashedLabel}
        </li>
      )}
    </ul>
  );
}

/**
 * Lines over time on one axis. `stepMs` = the bucket: a gap longer than two buckets breaks the line instead of
 * bridging it. `markers` draw dashed vertical rules (e.g. WMS version changes), labelled at the top. `dashed` series
 * (a comparison period shifted onto this axis) share their twin's colour; `dashedLabel` names them in the legend.
 * `onSelect(from, to)`: dragging across the plot (or Shift + arrows from the crosshair, then Enter) selects a span —
 * the caller zooms into it; spans shorter than `minSelectMs` are ignored. Touch: horizontal drags select, vertical
 * ones still scroll the page.
 */
export function LineChart({
  title,
  series,
  from,
  to,
  stepMs,
  format,
  markers = [],
  empty,
  height = H,
  dashedLabel,
  onSelect,
  minSelectMs = 0,
}: {
  title: string;
  series: LineSeries[];
  from: number;
  to: number;
  stepMs: number;
  format: (v: number) => string;
  markers?: Marker[];
  empty?: string;
  height?: number;
  dashedLabel?: string;
  onSelect?: (from: number, to: number) => void;
  minSelectMs?: number;
}) {
  const { t } = useConsoleI18n();
  const [wrap, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  // the span being selected (times), by pointer drag or by keyboard from `anchor` (an index into `times`)
  const [sel, setSel] = useState<{ a: number; b: number } | null>(null);
  const [anchor, setAnchor] = useState<number | null>(null);
  const shown = series.filter((s, i) => slot(s, i) < MAX_SERIES);
  const solid = shown.filter((s) => !s.dashed);
  const hasDashed = shown.length > solid.length;
  const times = useMemo(
    () =>
      [...new Set(shown.flatMap((s) => s.points.filter((p) => p.v != null).map((p) => p.t)))].sort(
        (a, b) => a - b
      ),
    [shown]
  );
  const values = shown.flatMap((s) => s.points.map((p) => p.v).filter((v): v is number => v != null));
  const ticks = niceTicks(Math.max(0, ...values) * 1.05);
  const yMax = ticks[ticks.length - 1] || 1;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const x = (t: number) => PAD.left + ((t - from) / Math.max(1, to - from)) * plotW;
  const y = (v: number) => PAD.top + plotH - (v / yMax) * plotH;
  const direct = solid.length >= 2 && solid.length <= 4 && !hasDashed;

  const path = (pts: LinePoint[]) => {
    let d = '';
    let prev: number | null = null;
    for (const p of pts) {
      if (p.v == null) {
        prev = null;
        continue;
      }
      const gap = prev === null || p.t - prev > stepMs * 2;
      d += `${gap ? 'M' : 'L'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`;
      prev = p.t;
    }
    return d;
  };
  const last = (pts: LinePoint[]) => [...pts].reverse().find((p) => p.v != null) ?? null;

  const timeAt = (clientX: number, rect: DOMRect) =>
    Math.min(
      to,
      Math.max(from, from + ((clientX - rect.left - PAD.left) / Math.max(1, plotW)) * (to - from))
    );
  const nearest = (clientX: number, rect: DOMRect) => {
    if (!times.length) return null;
    const t = timeAt(clientX, rect);
    let best = 0;
    for (let i = 1; i < times.length; i++) if (Math.abs(times[i] - t) < Math.abs(times[best] - t)) best = i;
    return best;
  };
  const pxPerMs = plotW / Math.max(1, to - from);
  const finish = (a: number, b: number) => {
    const lo = Math.max(from, Math.min(a, b));
    const hi = Math.min(to, Math.max(a, b));
    if (onSelect && hi - lo >= Math.max(minSelectMs, MIN_DRAG_PX / Math.max(pxPerMs, 1e-9))) onSelect(lo, hi);
  };
  const onDown = (e: PointerEvent<SVGSVGElement>) => {
    if (!onSelect || e.button !== 0) return;
    const t0 = timeAt(e.clientX, e.currentTarget.getBoundingClientRect());
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setSel({ a: t0, b: t0 });
  };
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHover(nearest(e.clientX, rect));
    if (sel) setSel({ a: sel.a, b: timeAt(e.clientX, rect) });
  };
  const onUp = () => {
    if (!sel) return;
    setSel(null);
    finish(sel.a, sel.b);
  };
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!times.length) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const d = e.key === 'ArrowLeft' ? -1 : 1;
      const at = hover ?? times.length - 1;
      const next = Math.min(times.length - 1, Math.max(0, at + d));
      setHover(next);
      if (onSelect && e.shiftKey) {
        // Shift + arrows: mark from where the crosshair was to where it goes, whole buckets
        const a = anchor ?? at;
        setAnchor(a);
        const lo = Math.min(times[a], times[next]);
        const hi = Math.max(times[a], times[next]);
        setSel({ a: lo, b: Math.min(to, hi + stepMs) });
      } else {
        setAnchor(null);
        setSel(null);
      }
    } else if (e.key === 'Enter' && sel && anchor != null) {
      e.preventDefault();
      setSel(null);
      setAnchor(null);
      finish(sel.a, sel.b);
    } else if (e.key === 'Escape') {
      setHover(null);
      setSel(null);
      setAnchor(null);
    }
  };
  const ht = hover != null ? times[hover] : null;
  const tipLeft = ht != null ? Math.min(Math.max(x(ht), 110), Math.max(110, width - 110)) : 0;
  const span = to - from;

  return (
    <figure className="nyk-usage nyk-chart" data-chart={title}>
      <figcaption className="nyk-usage-head">
        <h3>{title}</h3>
        <Legend
          items={solid.map((s) => ({ key: s.key, label: s.label, color: slot(s, shown.indexOf(s)) }))}
          dashedLabel={hasDashed ? dashedLabel : undefined}
        />
      </figcaption>
      <div className="nyk-usage-plot" ref={wrap}>
        {!values.length ? (
          <div className="nyk-usage-empty">{empty ?? t('chart.noData')}</div>
        ) : (
          width > 0 && (
            <svg
              width={width}
              height={height}
              role="img"
              aria-label={`${title}: ${shown.map((s) => s.label).join(', ')}`}
              aria-description={onSelect ? t('chart.select') : undefined}
              tabIndex={0}
              className={onSelect ? 'nyk-chart-selectable' : undefined}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={() => setSel(null)}
              onPointerLeave={() => !sel && setHover(null)}
              onKeyDown={onKey}
              onBlur={() => {
                setHover(null);
                setAnchor(null);
                setSel(null);
              }}
            >
              {ticks.map((v) => (
                <g key={v}>
                  <line className="nyk-usage-grid" x1={PAD.left} x2={PAD.left + plotW} y1={y(v)} y2={y(v)} />
                  <text className="nyk-usage-axis" x={PAD.left - 8} y={y(v)} dy="0.32em" textAnchor="end">
                    {format(v)}
                  </text>
                </g>
              ))}
              {[from, (from + to) / 2, to].map((t, i) => (
                <text
                  key={i}
                  className="nyk-usage-axis"
                  x={x(t)}
                  y={height - 6}
                  textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
                >
                  {timeLabel(t, span)}
                </text>
              ))}
              {markers
                .filter((m) => m.t >= from && m.t <= to)
                .map((m) => (
                  <g key={`${m.t}-${m.label}`} className="nyk-chart-marker">
                    <line x1={x(m.t)} x2={x(m.t)} y1={PAD.top} y2={PAD.top + plotH} />
                    <text x={x(m.t) + 4} y={PAD.top + 10}>
                      {m.label}
                    </text>
                  </g>
                ))}
              {shown.map((s, i) => (
                <path
                  key={s.key}
                  className={`nyk-chart-line ${seriesClass(slot(s, i))}${s.dashed ? ' nyk-chart-dashed' : ''}`}
                  d={path(s.points)}
                />
              ))}
              {direct &&
                directLabels(solid, last, x, y, PAD.left + plotW).map((l) => (
                  <text key={l.key} className="nyk-chart-direct" x={l.x} y={l.y} textAnchor="end">
                    <tspan className={`nyk-chart-direct-dot ${seriesClass(l.index)}`}>● </tspan>
                    {l.label}
                  </text>
                ))}
              {sel && sel.a !== sel.b && (
                <rect
                  className="nyk-chart-selection"
                  data-testid="chart-selection"
                  x={x(Math.min(sel.a, sel.b))}
                  y={PAD.top}
                  width={Math.abs(x(sel.b) - x(sel.a))}
                  height={plotH}
                />
              )}
              {ht != null && (
                <g className="nyk-usage-cross">
                  <line x1={x(ht)} x2={x(ht)} y1={PAD.top} y2={PAD.top + plotH} />
                  {shown.map((s, i) => {
                    const p = s.points.find((q) => q.t === ht);
                    return p?.v != null ? (
                      <circle key={s.key} className={seriesClass(slot(s, i))} cx={x(ht)} cy={y(p.v)} r={4} />
                    ) : null;
                  })}
                </g>
              )}
            </svg>
          )
        )}
        {ht != null && (
          <div className="nyk-usage-tip nyk-chart-tip" style={{ left: tipLeft }} role="status">
            <span>
              {sel && sel.a !== sel.b
                ? t('chart.selected', {
                    from: tipTime(Math.min(sel.a, sel.b)),
                    to: tipTime(Math.max(sel.a, sel.b)),
                  })
                : tipTime(ht)}
            </span>
            {shown.map((s, i) => {
              const p = s.points.find((q) => q.t === ht);
              return (
                <span key={s.key} className="nyk-chart-tip-row">
                  <span
                    className={`nyk-chart-swatch ${seriesClass(slot(s, i))}${s.dashed ? ' nyk-chart-swatch-dashed' : ''}`}
                    aria-hidden="true"
                  />
                  {s.label}: <strong>{p?.v != null ? format(p.v) : '—'}</strong>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </figure>
  );
}

export interface ScatterPoint {
  t: number;
  v: number;
  /** More than one (a batch) is named in the tooltip. */
  count?: number;
}
export interface ScatterSeries {
  key: string;
  label: string;
  points: ScatterPoint[];
  color?: number;
}

const tipSecond = (t: number) =>
  new Date(t).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

/**
 * Single measurements as dots over time on one axis (e.g. each call's duration): the pointer (or a tap, or the arrow
 * keys) picks the nearest dot and names its time, series, value and batch size. `onSelect` zooms like the
 * `LineChart`'s: drag across the plot, or Shift + arrows from the picked dot, then Enter.
 */
export function ScatterChart({
  title,
  series,
  from,
  to,
  format,
  empty,
  height = H,
  onSelect,
  minSelectMs = 0,
}: {
  title: string;
  series: ScatterSeries[];
  from: number;
  to: number;
  format: (v: number) => string;
  empty?: string;
  height?: number;
  onSelect?: (from: number, to: number) => void;
  minSelectMs?: number;
}) {
  const { t } = useConsoleI18n();
  const [wrap, width] = useWidth<HTMLDivElement>();
  // the picked dot (an index into `ordered`), the span being selected and the keyboard selection's start
  const [pick, setPick] = useState<number | null>(null);
  const [sel, setSel] = useState<{ a: number; b: number; x0: number } | null>(null);
  const [anchor, setAnchor] = useState<number | null>(null);
  const shown = series.filter((s, i) => (s.color ?? i) < MAX_SERIES);
  const ordered = useMemo(
    () =>
      shown
        .flatMap((s, si) => s.points.map((p) => ({ ...p, si })))
        .filter((p) => p.t >= from && p.t <= to)
        .sort((a, b) => a.t - b.t || a.v - b.v),
    [shown, from, to]
  );
  const ticks = niceTicks(Math.max(0, ...ordered.map((p) => p.v)) * 1.05);
  const yMax = ticks[ticks.length - 1] || 1;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const x = (tm: number) => PAD.left + ((tm - from) / Math.max(1, to - from)) * plotW;
  const y = (v: number) => PAD.top + plotH - (v / yMax) * plotH;
  const timeAt = (clientX: number, rect: DOMRect) =>
    Math.min(
      to,
      Math.max(from, from + ((clientX - rect.left - PAD.left) / Math.max(1, plotW)) * (to - from))
    );
  /** The dot nearest to the pointer, within 24 px. */
  const nearest = (clientX: number, clientY: number, rect: DOMRect) => {
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    let best: number | null = null;
    let dist = 24 * 24;
    ordered.forEach((p, i) => {
      const d = (x(p.t) - px) ** 2 + (y(p.v) - py) ** 2;
      if (d < dist) {
        dist = d;
        best = i;
      }
    });
    return best;
  };
  const pxPerMs = plotW / Math.max(1, to - from);
  const finish = (a: number, b: number) => {
    const lo = Math.max(from, Math.min(a, b));
    const hi = Math.min(to, Math.max(a, b));
    if (onSelect && hi - lo >= Math.max(minSelectMs, MIN_DRAG_PX / Math.max(pxPerMs, 1e-9))) onSelect(lo, hi);
  };
  const onDown = (e: PointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const t0 = timeAt(e.clientX, rect);
    setSel({ a: t0, b: t0, x0: e.clientX });
  };
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (sel) setSel({ ...sel, b: timeAt(e.clientX, rect) });
    else setPick(nearest(e.clientX, e.clientY, rect));
  };
  const onUp = (e: PointerEvent<SVGSVGElement>) => {
    if (!sel) return;
    setSel(null);
    // a tap (or a click) picks the dot under it; a drag selects a span
    if (Math.abs(e.clientX - sel.x0) < MIN_DRAG_PX)
      setPick(nearest(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect()));
    else finish(sel.a, sel.b);
  };
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!ordered.length) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const at = pick ?? ordered.length - 1;
      const next = Math.min(ordered.length - 1, Math.max(0, at + (e.key === 'ArrowLeft' ? -1 : 1)));
      setPick(next);
      if (onSelect && e.shiftKey) {
        const a = anchor ?? at;
        setAnchor(a);
        setSel({ a: ordered[a].t, b: ordered[next].t, x0: 0 });
      } else {
        setAnchor(null);
        setSel(null);
      }
    } else if (e.key === 'Enter' && sel && anchor != null) {
      e.preventDefault();
      setSel(null);
      setAnchor(null);
      finish(sel.a, sel.b);
    } else if (e.key === 'Escape') {
      setPick(null);
      setSel(null);
      setAnchor(null);
    }
  };
  const p = pick != null ? ordered[pick] : undefined;
  const tipLeft = p ? Math.min(Math.max(x(p.t), 110), Math.max(110, width - 110)) : 0;
  const span = to - from;
  const selecting = sel && sel.a !== sel.b;

  return (
    <figure className="nyk-usage nyk-chart" data-chart={title}>
      <figcaption className="nyk-usage-head">
        <h3>{title}</h3>
        <Legend items={shown.map((s, i) => ({ key: s.key, label: s.label, color: s.color ?? i }))} />
      </figcaption>
      <div className="nyk-usage-plot" ref={wrap}>
        {!ordered.length ? (
          <div className="nyk-usage-empty">{empty ?? t('chart.noData')}</div>
        ) : (
          width > 0 && (
            <svg
              width={width}
              height={height}
              role="img"
              aria-label={`${title}: ${shown.map((s) => `${s.label} (${s.points.length})`).join(', ')}`}
              aria-description={onSelect ? t('chart.select') : undefined}
              tabIndex={0}
              className={`nyk-chart-scatter${onSelect ? ' nyk-chart-selectable' : ''}`}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={() => setSel(null)}
              onKeyDown={onKey}
              onBlur={() => {
                setAnchor(null);
                setSel(null);
              }}
            >
              {ticks.map((v) => (
                <g key={v}>
                  <line className="nyk-usage-grid" x1={PAD.left} x2={PAD.left + plotW} y1={y(v)} y2={y(v)} />
                  <text className="nyk-usage-axis" x={PAD.left - 8} y={y(v)} dy="0.32em" textAnchor="end">
                    {format(v)}
                  </text>
                </g>
              ))}
              {[from, (from + to) / 2, to].map((tm, i) => (
                <text
                  key={i}
                  className="nyk-usage-axis"
                  x={x(tm)}
                  y={height - 6}
                  textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
                >
                  {timeLabel(tm, span)}
                </text>
              ))}
              {shown.map((s, si) => (
                <g key={s.key} className={`nyk-chart-dots ${seriesClass(s.color ?? si)}`}>
                  {s.points
                    .filter((q) => q.t >= from && q.t <= to)
                    .map((q, i) => (
                      <circle key={i} cx={x(q.t)} cy={y(q.v)} r={3} />
                    ))}
                </g>
              ))}
              {selecting && (
                <rect
                  className="nyk-chart-selection"
                  data-testid="chart-selection"
                  x={x(Math.min(sel.a, sel.b))}
                  y={PAD.top}
                  width={Math.abs(x(sel.b) - x(sel.a))}
                  height={plotH}
                />
              )}
              {p && (
                <g className="nyk-usage-cross">
                  <circle
                    className={seriesClass(shown[p.si].color ?? p.si)}
                    cx={x(p.t)}
                    cy={y(p.v)}
                    r={6}
                    data-testid="chart-picked"
                  />
                </g>
              )}
            </svg>
          )
        )}
        {(p || selecting) && (
          <div className="nyk-usage-tip nyk-chart-tip" style={{ left: tipLeft }} role="status">
            {selecting ? (
              <span>
                {t('chart.selected', {
                  from: tipSecond(Math.min(sel.a, sel.b)),
                  to: tipSecond(Math.max(sel.a, sel.b)),
                })}
              </span>
            ) : (
              p && (
                <>
                  <span>{tipSecond(p.t)}</span>
                  <span className="nyk-chart-tip-row">
                    <span
                      className={`nyk-chart-swatch ${seriesClass(shown[p.si].color ?? p.si)}`}
                      aria-hidden="true"
                    />
                    {shown[p.si].label}: <strong>{format(p.v)}</strong>
                    {p.count != null && p.count > 1 && ` (${t('chart.batch', { n: p.count })})`}
                  </span>
                </>
              )
            )}
          </div>
        )}
      </div>
    </figure>
  );
}

export interface Bar {
  key: string;
  label: string;
  v: number;
}

/** One series of bars (e.g. daily volumes): rounded tops on the baseline, hover tooltip per bar. */
export function BarChart({
  title,
  bars,
  format,
  empty,
  height = 180,
}: {
  title: string;
  bars: Bar[];
  format: (v: number) => string;
  empty?: string;
  height?: number;
}) {
  const { t } = useConsoleI18n();
  const [wrap, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const ticks = niceTicks(Math.max(0, ...bars.map((b) => b.v)) * 1.05);
  const yMax = ticks[ticks.length - 1] || 1;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const slot = bars.length ? plotW / bars.length : 0;
  const bw = Math.max(1, slot - 2);
  const y = (v: number) => PAD.top + plotH - (v / yMax) * plotH;
  const total = bars.reduce((a, b) => a + b.v, 0);
  const h = hover != null ? bars[hover] : null;
  const labelEvery = Math.max(1, Math.ceil(bars.length / 6));
  return (
    <figure className="nyk-usage nyk-chart" data-chart={title}>
      <figcaption className="nyk-usage-head">
        <h3>{title}</h3>
        <dl className="nyk-usage-stats">
          <div>
            <dt>Total</dt>
            <dd>{format(total)}</dd>
          </div>
          <div>
            <dt>Per reported day</dt>
            <dd>{bars.length ? format(total / bars.length) : '—'}</dd>
          </div>
        </dl>
      </figcaption>
      <div className="nyk-usage-plot" ref={wrap}>
        {!bars.length ? (
          <div className="nyk-usage-empty">{empty ?? t('chart.noData')}</div>
        ) : (
          width > 0 && (
            <svg
              width={width}
              height={height}
              role="img"
              aria-label={t('chart.total', { title, total: format(total) })}
              tabIndex={0}
              onPointerLeave={() => setHover(null)}
              onBlur={() => setHover(null)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                  e.preventDefault();
                  const d = e.key === 'ArrowLeft' ? -1 : 1;
                  setHover((cur) => Math.min(bars.length - 1, Math.max(0, (cur ?? bars.length - 1) + d)));
                }
              }}
            >
              {ticks.map((v) => (
                <g key={v}>
                  <line className="nyk-usage-grid" x1={PAD.left} x2={PAD.left + plotW} y1={y(v)} y2={y(v)} />
                  <text className="nyk-usage-axis" x={PAD.left - 8} y={y(v)} dy="0.32em" textAnchor="end">
                    {format(v)}
                  </text>
                </g>
              ))}
              {bars.map((b, i) => {
                const top = y(b.v);
                const bh = Math.max(0, PAD.top + plotH - top);
                const bx = PAD.left + i * slot + 1;
                const r = Math.min(4, bw / 2, bh);
                return (
                  <g key={b.key} onPointerEnter={() => setHover(i)}>
                    <rect
                      className="nyk-chart-hit"
                      x={PAD.left + i * slot}
                      y={PAD.top}
                      width={slot}
                      height={plotH}
                    />
                    {bh > 0 && (
                      <path
                        className={`nyk-chart-bar${hover === i ? ' nyk-chart-bar-on' : ''}`}
                        d={`M${bx},${top + bh}V${top + r}Q${bx},${top} ${bx + r},${top}H${bx + bw - r}Q${bx + bw},${top} ${bx + bw},${top + r}V${top + bh}Z`}
                      />
                    )}
                    {i % labelEvery === 0 && (
                      <text className="nyk-usage-axis" x={bx + bw / 2} y={height - 6} textAnchor="middle">
                        {b.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          )
        )}
        {h && hover != null && (
          <div
            className="nyk-usage-tip"
            style={{
              left: Math.min(Math.max(PAD.left + (hover + 0.5) * slot, 70), Math.max(70, width - 70)),
            }}
            role="status"
          >
            <strong>{format(h.v)}</strong>
            <span>{h.label}</span>
          </div>
        )}
      </div>
    </figure>
  );
}

/** A small trend of daily counts (error groups): bars only, the numbers are in the row next to it. */
export function Sparkline({ values, label }: { values: number[]; label: string }) {
  const max = Math.max(1, ...values);
  const w = 64;
  const h = 18;
  const slot = values.length ? w / values.length : w;
  return (
    <svg className="nyk-chart-spark" width={w} height={h} role="img" aria-label={label}>
      {values.map((v, i) => {
        const bh = v > 0 ? Math.max(1.5, (v / max) * h) : 0;
        return (
          <rect key={i} x={i * slot + 0.5} y={h - bh} width={Math.max(1, slot - 1)} height={bh} rx={1} />
        );
      })}
    </svg>
  );
}

export interface Slice {
  key: string;
  label: string;
  v: number;
}

/**
 * Parts of one whole (tasks by status): a ring of arcs in series order, the total in its middle, and a legend that
 * names every slice with its value, so the chart is readable without the colours. Slices without a value get no arc.
 */
export function DonutChart({
  title,
  slices,
  format,
  empty,
  size = 160,
}: {
  title: string;
  slices: Slice[];
  format: (v: number) => string;
  empty?: string;
  size?: number;
}) {
  const { t } = useConsoleI18n();
  const total = slices.reduce((a, s) => a + Math.max(0, s.v), 0);
  const stroke = Math.max(8, size * 0.16);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let at = 0;
  return (
    <figure className="nyk-chart nyk-donut" data-chart={title}>
      <figcaption className="nyk-usage-head">
        <h3>{title}</h3>
      </figcaption>
      {total <= 0 ? (
        <div className="nyk-usage-empty">{empty ?? t('chart.noData')}</div>
      ) : (
        <div className="nyk-donut-body">
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            role="img"
            aria-label={t('chart.total', { title, total: format(total) })}
          >
            <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
              {slices.map((s, i) => {
                if (s.v <= 0) return null;
                const len = (s.v / total) * c;
                const arc = (
                  <circle
                    key={s.key}
                    className={`nyk-donut-slice ${seriesClass(i)}`}
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    strokeWidth={stroke}
                    strokeDasharray={`${len} ${c - len}`}
                    strokeDashoffset={-at}
                  />
                );
                at += len;
                return arc;
              })}
            </g>
            <text className="nyk-donut-total" x="50%" y="50%" textAnchor="middle" dominantBaseline="central">
              {format(total)}
            </text>
          </svg>
          <ul className="nyk-chart-legend nyk-donut-legend" aria-label={t('chart.legend')}>
            {slices.map((s, i) => (
              <li key={s.key}>
                <span className={`nyk-chart-swatch ${seriesClass(i)}`} aria-hidden="true" />
                <span className="nyk-donut-label">{s.label}</span>
                <span className="nyk-donut-value">{format(s.v)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </figure>
  );
}
